import React, { useMemo, useState } from "react";
import { superService, SUPER_BOOST_FLOW } from "../../services";
import { BOOST_PLATFORM_MAP, BOOST_STATUS } from "../../config/addons";
import { useApp } from "../../context/AppContext";
import { useI18n } from "../../i18n";
import { Badge, Button, DataTable, PageHead, Select, money, formatDate, useAsync } from "../../components/ui";

// ออเดอร์ปั้มไลค์/ฟอลโลว์ของทุกร้าน · ยกเลิก = คืน point ให้ร้านอัตโนมัติ
export default function SuperBoosts() {
  const { profile, toast } = useApp();
  const { tx } = useI18n();
  const by = profile?.email || "super_user";
  const { data: orders, loading, reload } = useAsync(() => superService.boostOrders(), [], []);
  const [status, setStatus] = useState("pending");
  const [busyId, setBusyId] = useState("");

  const rows = useMemo(() => (orders || []).filter((row) => !status || row.status === status), [orders, status]);

  const change = async (row, next) => {
    if (next === "cancelled" && !window.confirm(tx(`ยกเลิกออเดอร์ ${row.orderNo} และคืน ${money(row.points, 0)} point ให้ร้าน?`, `Cancel ${row.orderNo} and refund ${money(row.points, 0)} points?`))) return;
    setBusyId(row.id);
    try {
      await superService.setBoostStatus(row, next, by);
      reload();
      toast(tx("อัปเดตสถานะแล้ว", "Status updated"), "success");
    } catch (err) {
      toast(err.message, "error");
    } finally {
      setBusyId("");
    }
  };

  return (
    <>
      <PageHead title={tx("ออเดอร์ปั้ม", "Boost orders")} subtitle={tx("ทำงานตามออเดอร์แล้วเปลี่ยนสถานะ ร้านเห็นสถานะทันที", "Work the orders and update status — shops see it right away")} />

      <div className="u-mb-16" style={{ maxWidth: 260 }}>
        <Select
          label={tx("สถานะ", "Status")}
          placeholder={tx("ทุกสถานะ", "All")}
          value={status}
          options={Object.entries(BOOST_STATUS).map(([value, label]) => ({ value, label: `${tx(label.th, label.en)} (${(orders || []).filter((row) => row.status === value).length})` }))}
          onChange={(event) => setStatus(event.target.value)}
        />
      </div>

      <DataTable
        loading={loading}
        rows={rows}
        columns={[
          { key: "createdAt", label: tx("วันที่", "Date"), render: (row) => formatDate(row.createdAt, true) },
          { key: "orgName", label: tx("ร้าน", "Shop") },
          {
            key: "orderNo",
            label: tx("ออเดอร์", "Order"),
            render: (row) => (
              <div style={{ maxWidth: 340 }}>
                <div className="u-strong">{row.orderNo} · {BOOST_PLATFORM_MAP[row.platform]?.name || row.platform} {row.type === "like" ? tx("ไลค์", "likes") : tx("ฟอลโลว์", "followers")} {money(row.quantity, 0)}</div>
                <div className="u-fs-12" style={{ wordBreak: "break-all" }}><a href={row.targetUrl} target="_blank" rel="noreferrer">{row.targetUrl}</a></div>
                {row.note ? <div className="u-fs-12 u-muted">{row.note}</div> : null}
              </div>
            ),
          },
          { key: "points", label: "Point", align: "right", render: (row) => money(row.points, 0) },
          { key: "status", label: tx("สถานะ", "Status"), render: (row) => <Badge tone={BOOST_STATUS[row.status]?.tone || "gray"}>{tx(BOOST_STATUS[row.status]?.th || row.status, BOOST_STATUS[row.status]?.en || row.status)}</Badge> },
          {
            key: "action",
            label: "",
            align: "right",
            render: (row) => (
              <div style={{ display: "flex", gap: 6, justifyContent: "flex-end", flexWrap: "wrap" }}>
                {(SUPER_BOOST_FLOW[row.status] || []).map((next) => (
                  <Button key={next} size="sm" variant={next === "completed" ? "primary" : "ghost"} disabled={busyId === row.id} onClick={() => change(row, next)}>
                    {tx(BOOST_STATUS[next].th, BOOST_STATUS[next].en)}
                  </Button>
                ))}
              </div>
            ),
          },
        ]}
        emptyText={tx("ไม่มีออเดอร์ในสถานะนี้", "No orders with this status")}
      />
    </>
  );
}
